import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MapPin,
  Phone,
  Mail,
  ArrowRight,
  ShieldCheck,
  KeyRound,
  Sparkles,
  Building2,
  ClipboardList,
  HelpCircle,
} from "lucide-react";
import { getProperty } from "@/lib/api";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FAQS = [
  {
    q: "How long does the application take?",
    a: "Most applicants complete the application in 8–12 minutes. Have your current address, employer info, and three personal references ready to make it faster.",
  },
  {
    q: "Is the $50 application fee refundable?",
    a: "The $50 fee is non-refundable. It covers the cost of background verification, credit review, and processing — whether your application is approved or not.",
  },
  {
    q: "How long until I hear back about my application?",
    a: "We review every application within 48 hours of receipt. You'll receive a response by email at the address you provided in your application.",
  },
  {
    q: "What information do I need to complete the application?",
    a: "You'll need: your date of birth, current address and landlord phone, employer + supervisor info, monthly income, and three personal references (name, phone, and relationship). Take a moment to gather these before you start.",
  },
  {
    q: "Do you accept applicants with pets?",
    a: "Pet policy varies by property. On the application you'll be asked whether you have pets and, for dogs, the breed(s). We'll evaluate on a case-by-case basis as part of the review.",
  },
  {
    q: "What are your income and credit requirements?",
    a: "As a general guideline, we look for a monthly income of roughly 3× the rent and a stable rental history. We evaluate every application holistically — solid references and steady employment can strengthen an application.",
  },
  {
    q: "How is my personal information handled?",
    a: "All information you submit is held in strict confidence and used solely to evaluate your application. Payment is processed by Stripe — we never see or store your card details. Experian Properties is an Equal Opportunity Housing provider and does not discriminate on any protected class.",
  },
  {
    q: "Can I apply for a property I haven't toured yet?",
    a: "Yes. Some applicants tour first, others apply first. If you'd like to schedule a tour before submitting, contact us using the phone number or email above.",
  },
  {
    q: "What happens after my application is approved?",
    a: "Once approved, our leasing team will reach out with next steps — signing the lease, security deposit, move-in date, and any final documentation. Everything is handled digitally where possible.",
  },
];

const HERO_IMG =
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400&auto=format&fit=crop";
const INT1 =
  "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600&auto=format&fit=crop";
const INT2 =
  "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&auto=format&fit=crop";

export default function Landing() {
  const [company, setCompany] = useState({
    name: "Experian Properties",
    tagline: "Quality rentals. Considered residents.",
    phone: "",
    email: "",
  });

  useEffect(() => {
    getProperty()
      .then(setCompany)
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF6F0] text-[#2A2521]" data-testid="landing-page">
      {/* NAV */}
      <nav
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#FAF6F0]/70 border-b border-[#E8DCC8]/60"
        data-testid="site-nav"
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-sm border border-[#A67C2E]/50 flex items-center justify-center">
              <KeyRound className="w-4 h-4 text-[#A67C2E]" />
            </div>
            <span
              className="font-heading text-lg tracking-wide"
              data-testid="brand-name"
            >
              {company.name}
            </span>
          </div>
          <div className="flex items-center gap-6">
            <a
              href="#faq"
              className="hidden sm:inline-block text-sm text-[#4A423B] hover:text-[#A67C2E] transition-colors"
              data-testid="nav-faq-link"
            >
              FAQ
            </a>
            <Link
              to="/apply"
              data-testid="nav-apply-btn"
              className="btn-gold px-5 py-2.5 rounded-sm text-sm inline-flex items-center gap-2"
            >
              Apply Now <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative min-h-[100vh] flex items-end overflow-hidden bg-[#F5EFE4]">
        <img
          src={HERO_IMG}
          alt="Warm modern home exterior"
          className="absolute inset-0 w-full h-full object-cover"
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 bg-gradient-to-t from-[#FAF6F0] via-[#FAF6F0]/85 to-[#FAF6F0]/25"
          aria-hidden="true"
        />
        <div className="absolute inset-0 hero-vignette" aria-hidden="true" />
        <div className="absolute inset-0 grain" aria-hidden="true" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-24 pt-40 w-full">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="label-eyebrow mb-6 text-[#A67C2E]"
            data-testid="hero-eyebrow"
          >
            Experian Properties · Now Accepting Applications
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-heading text-4xl sm:text-5xl lg:text-7xl max-w-4xl leading-[1.05] mb-6 text-[#2A2521]"
            data-testid="hero-title"
          >
            Find your next <em className="text-[#A67C2E] font-normal">address</em>.
            <br />
            Then reserve it.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35 }}
            className="text-[#4A423B] max-w-xl text-base sm:text-lg leading-relaxed mb-10"
          >
            {company.tagline} Complete a single application for any of our
            properties and pay the $50 processing fee in one considered step.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="flex flex-wrap items-center gap-4"
          >
            <Link
              to="/apply"
              data-testid="hero-apply-btn"
              className="btn-gold inline-flex items-center gap-2 px-8 py-4 rounded-sm text-sm"
            >
              Begin Application
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="#process"
              className="btn-ghost-gold inline-flex items-center gap-2 px-6 py-4 rounded-sm text-sm"
              data-testid="hero-details-btn"
            >
              How It Works
            </a>
          </motion.div>
        </div>
      </section>

      {/* COMPANY INFO BAR */}
      <section
        id="contact"
        className="relative border-y border-[#E8DCC8]/60 bg-[#F5EFE4]"
        data-testid="company-info-section"
      >
        <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-sm border border-[#A67C2E]/40 flex items-center justify-center shrink-0">
              <Building2 className="w-4 h-4 text-[#A67C2E]" />
            </div>
            <div>
              <p className="label-eyebrow">Company</p>
              <p
                className="mt-2 text-[#3A342E] text-sm leading-relaxed"
                data-testid="company-name"
              >
                {company.name}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-sm border border-[#A67C2E]/40 flex items-center justify-center shrink-0">
              <Phone className="w-4 h-4 text-[#A67C2E]" />
            </div>
            <div>
              <p className="label-eyebrow">Leasing</p>
              <p className="mt-2 text-[#3A342E] text-sm" data-testid="company-phone">
                {company.phone}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-sm border border-[#A67C2E]/40 flex items-center justify-center shrink-0">
              <Mail className="w-4 h-4 text-[#A67C2E]" />
            </div>
            <div>
              <p className="label-eyebrow">Correspondence</p>
              <p
                className="mt-2 text-[#3A342E] text-sm break-all"
                data-testid="company-email"
              >
                {company.email}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* BENTO */}
      <section className="max-w-7xl mx-auto px-6 py-24 md:py-32">
        <div className="mb-16 max-w-2xl">
          <p className="label-eyebrow text-[#A67C2E] mb-4">The Portfolio</p>
          <h2 className="font-heading text-3xl md:text-5xl leading-[1.1]">
            One application. <br />
            <em className="text-[#A67C2E] font-normal">Every address we manage.</em>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:h-[540px]">
          <div className="md:row-span-2 relative overflow-hidden rounded-sm group">
            <img
              src={INT1}
              alt="Living room"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <p className="label-eyebrow text-[#A67C2E] mb-2">01</p>
              <p className="font-heading text-2xl">Considered Interiors</p>
            </div>
          </div>
          <div className="relative overflow-hidden rounded-sm group">
            <img
              src={INT2}
              alt="Detail"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <p className="label-eyebrow text-[#A67C2E] mb-2">02</p>
              <p className="font-heading text-xl">Quality Materials</p>
            </div>
          </div>
          <div className="relative rounded-sm border border-[#E8DCC8] p-8 flex flex-col justify-between overflow-hidden bg-white">
            <Sparkles className="w-6 h-6 text-[#A67C2E]" aria-hidden="true" />
            <div>
              <p className="label-eyebrow text-[#A67C2E] mb-3">03</p>
              <p className="font-heading text-xl leading-tight">
                A short application. A considered response.
              </p>
              <p className="text-sm text-[#6B6560] mt-3">
                Applications reviewed within 48 hours.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" className="border-t border-[#E8DCC8]/60 bg-[#F5EFE4]">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <div>
              <p className="label-eyebrow text-[#A67C2E] mb-4">The Process</p>
              <h2 className="font-heading text-3xl md:text-5xl leading-[1.1] mb-6">
                Three steps to <em className="text-[#A67C2E] font-normal">reserve</em>.
              </h2>
              <p className="text-[#6B6560] text-base leading-relaxed max-w-md">
                Enter the address you&apos;re applying for, complete the application,
                remit the $50 fee, and await our response.
              </p>
            </div>
            <ol className="space-y-6">
              {[
                {
                  n: "I",
                  t: "Choose Your Address",
                  d: "Tell us which of our properties you'd like to apply for.",
                },
                {
                  n: "II",
                  t: "Complete Application",
                  d: "A short editorial questionnaire and the $50 processing fee via secure Stripe checkout.",
                },
                {
                  n: "III",
                  t: "Review & Response",
                  d: "We correspond within 48 hours of receipt.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="flex gap-6 pb-6 border-b border-[#E8DCC8]/60 last:border-0"
                  data-testid={`process-step-${s.n}`}
                >
                  <span className="font-heading text-3xl text-[#A67C2E] w-10 shrink-0">
                    {s.n}
                  </span>
                  <div>
                    <p className="font-heading text-xl mb-1">{s.t}</p>
                    <p className="text-sm text-[#6B6560]">{s.d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        id="faq"
        className="border-t border-[#E8DCC8]/60 bg-[#FAF6F0]"
        data-testid="faq-section"
      >
        <div className="max-w-4xl mx-auto px-6 py-24 md:py-32">
          <div className="mb-12 flex items-start gap-4">
            <HelpCircle
              className="w-5 h-5 text-[#A67C2E] shrink-0 mt-2"
              aria-hidden="true"
            />
            <div>
              <p className="label-eyebrow text-[#A67C2E] mb-3">
                Frequently Asked
              </p>
              <h2 className="font-heading text-3xl md:text-5xl leading-[1.1]">
                Questions we hear <em className="text-[#A67C2E] font-normal">most</em>.
              </h2>
            </div>
          </div>

          <Accordion
            type="single"
            collapsible
            className="border-t border-[#E8DCC8]/60"
            data-testid="faq-accordion"
          >
            {FAQS.map((item, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="border-b border-[#E8DCC8]/60"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger
                  className="py-6 text-left font-heading text-lg md:text-xl text-[#2A2521] hover:no-underline hover:text-[#A67C2E] transition-colors"
                  data-testid={`faq-trigger-${i}`}
                >
                  {item.q}
                </AccordionTrigger>
                <AccordionContent
                  className="pb-6 text-sm md:text-base text-[#6B6560] leading-relaxed max-w-3xl"
                  data-testid={`faq-content-${i}`}
                >
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="mt-12 text-center">
            <p className="text-sm text-[#8B8378] mb-3">Still have questions?</p>
            <a
              href={`mailto:${company.email}`}
              className="text-[#A67C2E] hover:text-[#8B6820] text-sm inline-flex items-center gap-2 transition-colors"
              data-testid="faq-contact-link"
            >
              <Mail className="w-4 h-4" />
              {company.email}
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-6 py-32 text-center">
        <ShieldCheck className="w-8 h-8 text-[#A67C2E] mx-auto mb-6" aria-hidden="true" />
        <h2 className="font-heading text-3xl md:text-5xl leading-[1.1] mb-6">
          Your address, <em className="text-[#A67C2E] font-normal">reserved.</em>
        </h2>
        <p className="text-[#6B6560] mb-10 max-w-md mx-auto">
          Payment is processed securely by Stripe. Your information is held in strict
          confidence.
        </p>
        <Link
          to="/apply"
          data-testid="cta-apply-btn"
          className="btn-gold inline-flex items-center gap-2 px-10 py-4 rounded-sm"
        >
          Begin Application <ArrowRight className="w-4 h-4" />
        </Link>
      </section>

      {/* EQUAL OPPORTUNITY DISCLAIMER */}
      <section
        className="border-t border-[#E8DCC8]/60 bg-[#FAF6F0]"
        data-testid="eo-housing-disclaimer"
      >
        <div className="max-w-4xl mx-auto px-6 py-14 flex items-start gap-4">
          <ClipboardList
            className="w-5 h-5 text-[#A67C2E] shrink-0 mt-2"
            aria-hidden="true"
          />
          <div>
            <p
              className="font-heading text-2xl md:text-3xl text-[#2A2521] mb-3 tracking-wide"
              data-testid="eo-housing-caps-landing"
            >
              WE ARE AN EQUAL OPPORTUNITY HOUSING PROVIDER.
            </p>
            <p className="text-xs text-[#6B6560] leading-relaxed">
              <strong className="text-[#3A342E]">Experian Properties</strong>{" "}
              does not discriminate on the basis of race, color, religion,
              national origin, sex, familial status, disability, or any other
              protected class. All information submitted through this
              application is held in strict confidence and used solely for the
              purpose of evaluating your application.
            </p>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#E8DCC8]/60 py-10 text-center text-xs text-[#8B8378]">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap items-center justify-between gap-4">
          <p>
            © {new Date().getFullYear()} {company.name}. All rights reserved.
          </p>
          <Link
            to="/admin"
            data-testid="footer-admin-link"
            className="text-[#8B8378] hover:text-[#A67C2E] transition-colors"
          >
            Admin
          </Link>
        </div>
      </footer>
    </div>
  );
}
